// Color Utilities
//
// Small, dependency-free helpers for deriving related colors at runtime. Used so
// that a single "routine color" can yield a family of clearly-related task shades
// (bundled but differentiable), and so views can tint backgrounds from a base hex.

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/** Parse a #rgb or #rrggbb hex string into RGB. Falls back to a neutral grey. */
export function hexToRgb(hex: string): RGB {
  if (!hex) return { r: 128, g: 128, b: 128 };
  let h = hex.trim().replace('#', '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length !== 6) return { r: 128, g: 128, b: 128 };
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return { r: 128, g: 128, b: 128 };
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));

export function rgbToHex({ r, g, b }: RGB): string {
  const toHex = (v: number) => clamp(v).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** rgba() string from a hex + alpha (0..1). Handy for tinted backgrounds. */
export function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Mix a color toward white by `amount` (0..1). */
export function lighten(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({
    r: r + (255 - r) * amount,
    g: g + (255 - g) * amount,
    b: b + (255 - b) * amount,
  });
}

/** Mix a color toward black by `amount` (0..1). */
export function darken(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({
    r: r * (1 - amount),
    g: g * (1 - amount),
    b: b * (1 - amount),
  });
}

/** Relative luminance (0..1), used to pick readable text color over a fill. */
export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Pick black or white text for legibility over a given background hex. */
export function readableTextColor(hex: string): string {
  return luminance(hex) > 0.6 ? '#1F1F22' : '#FFFFFF';
}

/**
 * Derive a shade of a base color for the task at `index` within a routine.
 *
 * All shades share the routine's hue (so tasks read as one family) but step
 * progressively lighter, giving each task a distinct-yet-bundled tone. Index 0
 * returns the base color unchanged.
 */
export function shadeForIndex(baseHex: string, index: number): string {
  if (index <= 0) return baseHex;
  // Step lighter each task, capped so later tasks stay clearly tinted (not white).
  const amount = Math.min(0.5, index * 0.13);
  return lighten(baseHex, amount);
}
